import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Clock, Play, Square, CheckCircle2, Calendar, Plane, Receipt, Download,
  User, Building2, Landmark, TrendingUp, CalendarClock, Wallet
} from "lucide-react";

const TABS = [
  { key: "attendance", label: "Attendance", icon: CalendarClock },
  { key: "leave",      label: "Leave",      icon: Plane },
  { key: "payslips",   label: "Payslips",   icon: Receipt },
];

const LEAVE_TYPES = ["Casual","Earned","Medical","Restricted"];

function fmtRupees(n) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

export default function EmployeeProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [today, setToday] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [tab, setTab] = useState("attendance");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ type: "Casual", from_date: "", to_date: "", reason: "" });

  const loadAll = async () => {
    const [p, t, a, l, s] = await Promise.all([
      api.get("/hr/self/profile"),
      api.get("/hr/self/today"),
      api.get("/hr/self/attendance"),
      api.get("/hr/self/leave"),
      api.get("/hr/self/payslips"),
    ]);
    setProfile(p.data); setToday(t.data.today); setAttendance(a.data.items);
    setLeaves(l.data.items); setPayslips(s.data.items);
  };

  useEffect(() => { loadAll().catch(()=>{}); }, []);

  const clock = async (action) => {
    setMsg(""); setBusy(true);
    try {
      await api.post("/hr/self/clock", { action });
      await loadAll();
      setMsg(action === "in" ? "Clocked in successfully." : "Clocked out. Have a good day!");
    } catch (e) { setMsg(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const submitLeave = async (e) => {
    e.preventDefault(); setMsg(""); setBusy(true);
    try {
      await api.post("/hr/self/leave", leaveForm);
      setLeaveForm({ type: "Casual", from_date: "", to_date: "", reason: "" });
      const l = await api.get("/hr/self/leave");
      setLeaves(l.data.items);
      setMsg("Leave request submitted for approval.");
    } catch (e) { setMsg(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!profile) return <div className="text-sm text-muted-foreground">Loading your profile…</div>;

  const clockedIn = !!today?.in_time && !today?.out_time;
  const doneToday = !!today?.out_time;
  const monthPresent = attendance.filter(a => a.in_time).length;

  return (
    <div className="space-y-6">
      <div>
        <div className="label-tiny text-[hsl(var(--primary))]">Employee Self-Service</div>
        <h1 className="mt-1 text-3xl font-heading font-bold">My Profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">Attendance · Leave · Payslips — all in one place.</p>
      </div>

      {/* Header cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Identity */}
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6" data-testid="profile-identity-card">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-2xl bg-peda-navy text-white grid place-items-center font-heading font-bold text-lg">
              {user?.name?.split(" ").map(w=>w[0]).slice(0,2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-xl">{user?.name}</div>
              <div className="text-sm text-muted-foreground">{profile.designation} · {profile.department}</div>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                <span className="stat-pill bg-muted text-muted-foreground font-mono">{profile.emp_code}</span>
                <span className="stat-pill bg-muted text-muted-foreground">Joined {profile.date_joined}</span>
                <span className="stat-pill bg-muted text-muted-foreground">Reports to: {profile.reporting_to}</span>
              </div>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <MiniStat icon={User} label="Employee ID" v={profile.emp_code}/>
            <MiniStat icon={Building2} label="Department" v={profile.department}/>
            <MiniStat icon={Landmark} label="Bank" v={`${profile.bank} · ${profile.account}`}/>
            <MiniStat icon={Wallet} label="Basic Pay" v={fmtRupees(profile.basic_pay)}/>
          </div>
          <div className="mt-4 grid grid-cols-4 gap-3">
            {Object.entries(profile.leave_balance || {}).map(([k,v])=>(
              <div key={k} className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
                <div className="font-heading font-bold text-lg">{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Clock-in card */}
        <div className="rounded-2xl border-2 border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/5 to-transparent p-6" data-testid="clock-in-card">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-[hsl(var(--primary))] text-white grid place-items-center"><Clock className="h-5 w-5"/></div>
            <div>
              <div className="label-tiny">Today</div>
              <div className="font-heading font-semibold">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-card border border-border p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Clock-in</div>
              <div className="font-heading font-bold text-2xl mt-1">{today?.in_time || "—"}</div>
            </div>
            <div className="rounded-lg bg-card border border-border p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Clock-out</div>
              <div className="font-heading font-bold text-2xl mt-1">{today?.out_time || "—"}</div>
            </div>
          </div>

          <div className="mt-5">
            {!today?.in_time && (
              <button data-testid="clock-in-button" disabled={busy} onClick={()=>clock("in")} className="btn-primary w-full justify-center">
                <Play className="h-4 w-4"/> Clock In
              </button>
            )}
            {clockedIn && (
              <button data-testid="clock-out-button" disabled={busy} onClick={()=>clock("out")} className="btn-primary w-full justify-center bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90">
                <Square className="h-4 w-4"/> Clock Out
              </button>
            )}
            {doneToday && (
              <div className="rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-3 py-2.5 text-sm inline-flex items-center gap-2 w-full justify-center font-medium">
                <CheckCircle2 className="h-4 w-4"/> Day complete · {today?.hours}h logged
              </div>
            )}
          </div>

          {msg && <div className="mt-3 text-xs text-center text-muted-foreground">{msg}</div>}

          <div className="mt-5 pt-4 border-t border-border/60 grid grid-cols-3 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">This month</div>
              <div className="font-heading font-bold text-lg">{monthPresent}<span className="text-xs text-muted-foreground">/30</span></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Leave used</div>
              <div className="font-heading font-bold text-lg">{leaves.filter(l=>l.status !== "rejected").length}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Payslips</div>
              <div className="font-heading font-bold text-lg">{payslips.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex rounded-full border border-border p-1 bg-muted/50 max-w-md text-sm">
        {TABS.map(t=>(
          <button
            key={t.key} onClick={()=>setTab(t.key)}
            data-testid={`profile-tab-${t.key}`}
            className={`flex-1 px-3 py-1.5 rounded-full inline-flex items-center justify-center gap-1.5 ${tab===t.key ? "bg-background shadow" : ""}`}
          >
            <t.icon className="h-3.5 w-3.5"/>{t.label}
          </button>
        ))}
      </div>

      {tab === "attendance" && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-5 border-b border-border/60">
            <div className="font-heading font-semibold">Attendance Log</div>
            <div className="text-xs text-muted-foreground">Last 60 entries</div>
          </div>
          {attendance.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No attendance yet. Clock in above to record your first entry.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr>
                  <th className="table-th">Date</th>
                  <th className="table-th">In</th>
                  <th className="table-th">Out</th>
                  <th className="table-th">Hours</th>
                  <th className="table-th">Status</th>
                </tr></thead>
                <tbody>
                  {attendance.map(a=>(
                    <tr key={a.id} className="border-t border-border">
                      <td className="table-cell font-medium">{a.date}</td>
                      <td className="table-cell font-mono">{a.in_time || "—"}</td>
                      <td className="table-cell font-mono">{a.out_time || "—"}</td>
                      <td className="table-cell">{a.hours || 0}h</td>
                      <td className="table-cell">
                        {a.out_time
                          ? <span className="badge-status-open">Complete</span>
                          : <span className="badge-status-pending">In progress</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "leave" && (
        <div className="grid lg:grid-cols-3 gap-4">
          <form onSubmit={submitLeave} className="rounded-2xl border border-border bg-card p-5 space-y-3">
            <div className="font-heading font-semibold">New leave request</div>
            <label className="block">
              <span className="text-xs font-semibold text-muted-foreground">Type</span>
              <select value={leaveForm.type} onChange={(e)=>setLeaveForm({...leaveForm, type:e.target.value})} className="input mt-1">
                {LEAVE_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-muted-foreground">From</span>
              <input required type="date" value={leaveForm.from_date} onChange={(e)=>setLeaveForm({...leaveForm, from_date:e.target.value})} className="input mt-1"/>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-muted-foreground">To</span>
              <input required type="date" value={leaveForm.to_date} onChange={(e)=>setLeaveForm({...leaveForm, to_date:e.target.value})} className="input mt-1"/>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-muted-foreground">Reason</span>
              <textarea required rows={3} value={leaveForm.reason} onChange={(e)=>setLeaveForm({...leaveForm, reason:e.target.value})} className="input mt-1" placeholder="Briefly describe the reason…"/>
            </label>
            <button data-testid="leave-submit-button" disabled={busy} type="submit" className="btn-primary w-full justify-center">
              <Plane className="h-4 w-4"/>Submit request
            </button>
          </form>

          <div className="lg:col-span-2 rounded-2xl border border-border bg-card overflow-hidden">
            <div className="p-5 border-b border-border/60">
              <div className="font-heading font-semibold">Your leave requests</div>
              <div className="text-xs text-muted-foreground">{leaves.length} total</div>
            </div>
            {leaves.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">No leave requests yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr>
                    <th className="table-th">Type</th>
                    <th className="table-th">From</th>
                    <th className="table-th">To</th>
                    <th className="table-th">Reason</th>
                    <th className="table-th">Status</th>
                  </tr></thead>
                  <tbody>
                    {leaves.map(l=>(
                      <tr key={l.id} className="border-t border-border">
                        <td className="table-cell font-medium">{l.type}</td>
                        <td className="table-cell font-mono text-xs">{l.from_date}</td>
                        <td className="table-cell font-mono text-xs">{l.to_date}</td>
                        <td className="table-cell text-muted-foreground line-clamp-1 max-w-xs">{l.reason}</td>
                        <td className="table-cell">
                          <span className={`stat-pill capitalize ${l.status === "approved" ? "bg-emerald-500/10 text-emerald-500" : l.status === "rejected" ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-500"}`}>{l.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "payslips" && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-heading font-semibold">Payslips</div>
              <div className="text-xs text-muted-foreground">Last 6 months</div>
            </div>
            <span className="stat-pill bg-emerald-500/10 text-emerald-500"><CheckCircle2 className="h-3 w-3"/>All paid</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                <th className="table-th">Month</th>
                <th className="table-th">Basic</th>
                <th className="table-th">HRA</th>
                <th className="table-th">DA</th>
                <th className="table-th">Gross</th>
                <th className="table-th">PF</th>
                <th className="table-th">Tax</th>
                <th className="table-th">Net Pay</th>
                <th className="table-th"></th>
              </tr></thead>
              <tbody>
                {payslips.map(p=>(
                  <tr key={p.month} className="border-t border-border">
                    <td className="table-cell font-medium">{p.month}</td>
                    <td className="table-cell font-mono text-xs">{fmtRupees(p.basic)}</td>
                    <td className="table-cell font-mono text-xs">{fmtRupees(p.hra)}</td>
                    <td className="table-cell font-mono text-xs">{fmtRupees(p.da)}</td>
                    <td className="table-cell font-mono text-xs font-semibold">{fmtRupees(p.gross)}</td>
                    <td className="table-cell font-mono text-xs text-red-500">-{fmtRupees(p.pf)}</td>
                    <td className="table-cell font-mono text-xs text-red-500">-{fmtRupees(p.tax)}</td>
                    <td className="table-cell font-mono font-heading font-bold text-[hsl(var(--primary))]">{fmtRupees(p.net_pay)}</td>
                    <td className="table-cell text-right">
                      <button className="btn-outline text-xs" onClick={()=>window.print()} data-testid={`payslip-download-${p.month.replace(/\s/g,'-')}`}>
                        <Download className="h-3.5 w-3.5"/>PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, label, v }) {
  return (
    <div className="flex items-start gap-2">
      <div className="h-8 w-8 rounded-md bg-muted grid place-items-center text-muted-foreground"><Icon className="h-4 w-4"/></div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="font-medium truncate">{v}</div>
      </div>
    </div>
  );
}
